import { Suspense, lazy } from 'react'
import { Navigate, useRoutes, useLocation } from 'react-router-dom'
import DashboardLayout from './layouts/dashboard'
import LogoOnlyLayout from './layouts/LogoOnlyLayout'

import GuestGuard from './guards/GuestGuard'
import AuthGuard from './guards/AuthGuard'

import LoadingScreen from './components/LoadingScreen'

import {
  PATH_AFTER_ADMIN_LOGIN,
  PATH_AFTER_STARTUP_ADMIN_LOGIN,
  PATH_AFTER_INVESTOR_ADMIN_LOGIN,
  PATH_AFTER_CONFORMER_ADMIN_LOGIN,
  PATH_AFTER_SUPERVISOR_ADMIN_LOGIN,
} from './config'
import ComplaintList from "./pages/admin/complaint-list/ComplaintList";
import CommentList from "./pages/admin/comment-list/CommentList";

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation()

  return (
    <Suspense
      fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}
    >
      <Component {...props} />
    </Suspense>
  )
}

export default function Router() {
  return useRoutes([
    {
      path: '/',
      children: [{ path: '/', element: <Navigate to='/auth/login' /> }],
    },
    {
      path: '/auth',
      element: <LogoOnlyLayout />,
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'verify',
          element: (
            <GuestGuard>
              <VerifyCode />
            </GuestGuard>
          ),
        },
      ],
    },
    {
      path: '/dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          element: <Navigate to={PATH_AFTER_ADMIN_LOGIN} replace />,
          index: true,
        },
        { path: 'app', element: <Dashboard /> },
        { path: 'profile', element: <Profile /> },
        {
          path: 'startup-verification/list',
          element: <StartupVerificationList />,
        },
        {
          path: 'startup-verification/detail/:id',
          element: <StartupVerificationDetails />,
        },
        {
          path: 'investor-verification/list',
          element: <InvestorVerificationList />,
        },
        {
          path: 'investor-verification/detail/:id',
          element: <InvestorVerificationDetails />,
        },
        {
          path: 'startup-valuation/list',
          element: <StartupValuationList />,
        },
        {
          path: 'investing-requests/start-up/list',
          element: <InvestingRequestsList />,
        },
        {
          path: 'investing-requests/list/:id',
          element: <InvestingRequestsDetails />,
        },
        {
          path: 'manage-startup/list',
          element: <ManageStartupList />,
        },
        {
          path: 'manage-startup/action/:id',
          element: <ManageStartupDetails />,
        },
        {
          path: 'manage-startup/round/data/:startupId/:investorManager',
          element: <ManageRoundList />,
        },
        {
          path: 'manage-startup/round/list/:startupId/:roundId',
          element: <ManagePhaseList />,
        },
        {
          path: 'manage-startup/round/list/:startupId/:roundId/:phaseId',
          element: <ManageTargetList />,
        },
        {
          path: 'manage-startup/round/list/:startupId/:roundId/:phaseId/:targetId',
          element: <ManageHistoryList />,
        },
        {
          path: 'manage-investor/list',
          element: <ManageInvestorList />,
        },
        {
          path: 'manage-complaint/list',
          element: <ManageComplaintDetails />,
        },
        {
          path: 'manage-comment/list/:id',
          element: <ManageCommentDetails />,
        },
        {
          path: 'new-startup',
          element: <ManageNewStartup />,
        },
        {
          path: 'manage-investor/detail/:id',
          element: <ManageInvestorDetails />,
        },
        {
          path: 'manage-investor/request-invest/:id',
          element: <RequestInvestList />,
        },
        {
          path: 'users/list',
          element: <UsersList />,
        },
        {
          path: 'admins/list',
          element: <AdminsList />,
        },
        {
          path: 'ticket/list',
          element: <TicketsList />,
        },
        {
          path: 'ticket/:id',
          element: <TicketsDetails />,
        },
        {
          path: 'categories/list',
          element: <CategoriesList />,
        },
        {
          path: 'invoices/list',
          element: <InvoicesList />,
        },
        {
          path: 'news/list',
          element: <NewsList />,
        },
        {
          path: 'receipts/list',
          element: <ReceiptsList />,
        },
        {
          path: 'receipts/list',
          element: <ReceiptsList />,
        },
        {
          path: 'setting',
          element: <Setting />,
        },
      ],
    },
    {
      path: '/startup/dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          element: <Navigate to={PATH_AFTER_STARTUP_ADMIN_LOGIN} replace />,
          index: true,
        },
        { path: 'app', element: <StartupAdminDashboard /> },
        { path: 'profile', element: <StartupAdminProfile /> },
        {
          path: 'startup-verification/list',
          element: <StartupAdminStartupVerificationList />,
        },
        {
          path: 'startup-verification/detail/:id',
          element: <StartupAdminStartupVerificationDetails />,
        },
        {
          path: 'manage-startup/list',
          element: <StartupAdminManageStartupList />,
        },
        {
          path: 'manage-startup/action/:id',
          element: <StartupAdminManageStartupDetails />,
        },
        {
          path: 'manage-startup/round/data/:startupId/:investorManager',
          element: <StartupAdminManageRoundList />,
        },
        {
          path: 'manage-startup/round/list/:startupId/:roundId',
          element: <StartupAdminManagePhaseList />,
        },
        {
          path: 'manage-startup/round/list/:startupId/:roundId/:phaseId',
          element: <StartupAdminManageTargetList />,
        },
        {
          path: 'manage-startup/round/list/:startupId/:roundId/:phaseId/:targetId',
          element: <StartupAdminManageHistoryList />,
        },
        {
          path: 'ticket/list',
          element: <StartupAdminTicketsList />,
        },
        {
          path: 'ticket/:id',
          element: <StartupAdminTicketsDetails />,
        },
        {
          path: 'news/list',
          element: <StartupAdminNewsList />,
        },
        {
          path: 'setting',
          element: <StartupAdminSetting />,
        },
      ],
    },
    {
      path: '/investor/dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          element: <Navigate to={PATH_AFTER_INVESTOR_ADMIN_LOGIN} replace />,
          index: true,
        },
        { path: 'app', element: <StartupAdminDashboard /> },
        { path: 'profile', element: <StartupAdminProfile /> },
        {
          path: 'investor-verification/list',
          element: <InvestorAdminInvestorVerificationList />,
        },
        {
          path: 'investor-verification/detail/:id',
          element: <InvestorAdminInvestorVerificationDetails />,
        },
        {
          path: 'manage-investor/list',
          element: <InvestorAdminManageInvestorList />,
        },
        {
          path: 'manage-investor/detail/:id',
          element: <InvestorAdminManageInvestorDetails />,
        },
        {
          path: 'manage-investor/request-invest/:id',
          element: <InvestorAdminRequestInvestList />,
        },
        {
          path: 'ticket/list',
          element: <InvestorAdminTicketsList />,
        },
        {
          path: 'ticket/:id',
          element: <InvestorAdminTicketsDetails />,
        },
        {
          path: 'setting',
          element: <StartupAdminSetting />,
        },
      ],
    },
    {
      path: '/conformer/dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          element: <Navigate to={PATH_AFTER_CONFORMER_ADMIN_LOGIN} replace />,
          index: true,
        },
        { path: 'app', element: <ConformerAdminDashboard /> },
        { path: 'profile', element: <ConformerAdminProfile /> },
        {
          path: 'setting',
          element: <ConformerAdminSetting />,
        },
      ],
    },
    {
      path: '/supervisor/dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          element: <Navigate to={PATH_AFTER_SUPERVISOR_ADMIN_LOGIN} replace />,
          index: true,
        },
        { path: 'app', element: <SupervisorAdminDashboard /> },
        { path: 'profile', element: <SupervisorAdminProfile /> },
        {
          path: 'setting',
          element: <SupervisorAdminSetting />,
        },
      ],
    },
    { path: '404', element: <NotFound /> },
    { path: '*', element: <Navigate to='/404' replace /> },
  ])
}

const Login = Loadable(lazy(() => import('./pages/auth/Login')))
const VerifyCode = Loadable(lazy(() => import('./pages/auth/VerifyCode')))
const Dashboard = Loadable(lazy(() => import('./pages/admin/DashboardApp')))
const Profile = Loadable(lazy(() => import('./pages/admin/Profile')))
const StartupVerificationList = Loadable(
  lazy(() =>
    import('./pages/admin/startup-verification/StartupVerificationList')
  )
)
const StartupVerificationDetails = Loadable(
  lazy(() =>
    import('./pages/admin/startup-verification/StartupVerificationDetails')
  )
)
const InvestorVerificationList = Loadable(
  lazy(() =>
    import('./pages/admin/investor-verification/InvestorVerificationList')
  )
)
const InvestorVerificationDetails = Loadable(
  lazy(() =>
    import('./pages/admin/investor-verification/InvestorVerificationDetails')
  )
)
const StartupValuationList = Loadable(
  lazy(() => import('./pages/admin/startup-valuation/StartupValuationList'))
)
const InvestingRequestsList = Loadable(
  lazy(() => import('./pages/admin/investing-requests/InvestingRequestsList'))
)
const InvestingRequestsDetails = Loadable(
  lazy(() =>
    import('./pages/admin/investing-requests/InvestingRequestsDetails')
  )
)
const ManageStartupList = Loadable(
  lazy(() => import('./pages/admin/manage-startup/ManageStartupList'))
)
const ManageStartupDetails = Loadable(
  lazy(() => import('./pages/admin/manage-startup/ManageStartupDetails'))
)
const ManageNewStartup = Loadable(
  lazy(() => import('./pages/admin/manage-startup/AddStartup'))
)
const ManageRoundList = Loadable(
  lazy(() => import('./pages/admin/manage-startup/ManageRoundList'))
)
const ManagePhaseList = Loadable(
  lazy(() => import('./pages/admin/manage-startup/ManagePhaseList'))
)
const ManageTargetList = Loadable(
  lazy(() => import('./pages/admin/manage-startup/ManageTargetList'))
)
const ManageHistoryList = Loadable(
  lazy(() => import('./pages/admin/manage-startup/ManageHistoryList'))
)
const ManageInvestorList = Loadable(
  lazy(() => import('./pages/admin/manage-investor/ManageInvestorList'))
)
const RequestInvestList = Loadable(
  lazy(() => import('./pages/admin/manage-investor/RequestInvestList'))
)
const ManageInvestorDetails = Loadable(
  lazy(() => import('./pages/admin/manage-investor/ManageInvestorDetails'))
)
const ManageComplaintDetails = Loadable(
    lazy(() => import('./pages/admin/complaint-list/ComplaintList'))
)
const ManageCommentDetails = Loadable(
    lazy(() => import('./pages/admin/comment-list/CommentList'))
)

const UsersList = Loadable(lazy(() => import('./pages/admin/users/UsersList')))
const AdminsList = Loadable(
  lazy(() => import('./pages/admin/admins/AdminList'))
)
const TicketsList = Loadable(
  lazy(() => import('./pages/admin/tickets/TicketsList'))
)
const TicketsDetails = Loadable(
  lazy(() => import('./pages/admin/tickets/TicketsDetails'))
)
const CategoriesList = Loadable(
  lazy(() => import('./pages/admin/categories/CategoriesList'))
)
const InvoicesList = Loadable(
  lazy(() => import('./pages/admin/invoices/InvoicesList'))
)
const NewsList = Loadable(lazy(() => import('./pages/admin/news/NewsList')))
const ReceiptsList = Loadable(
  lazy(() => import('./pages/admin/receipts/ReceiptsList'))
)
const Setting = Loadable(lazy(() => import('./pages/admin/Setting')))

const StartupAdminDashboard = Loadable(
  lazy(() => import('./pages/startup/DashboardApp'))
)
const StartupAdminProfile = Loadable(
  lazy(() => import('./pages/startup/Profile'))
)
const StartupAdminStartupVerificationList = Loadable(
  lazy(() =>
    import('./pages/startup/startup-verification/StartupVerificationList')
  )
)
const StartupAdminStartupVerificationDetails = Loadable(
  lazy(() =>
    import('./pages/startup/startup-verification/StartupVerificationDetails')
  )
)
const StartupAdminManageStartupList = Loadable(
  lazy(() => import('./pages/startup/manage-startup/ManageStartupList'))
)
const StartupAdminManageStartupDetails = Loadable(
  lazy(() => import('./pages/startup/manage-startup/ManageStartupDetails'))
)
const StartupAdminManageRoundList = Loadable(
  lazy(() => import('./pages/startup/manage-startup/ManageRoundList'))
)
const StartupAdminManagePhaseList = Loadable(
  lazy(() => import('./pages/startup/manage-startup/ManagePhaseList'))
)
const StartupAdminManageTargetList = Loadable(
  lazy(() => import('./pages/startup/manage-startup/ManageTargetList'))
)
const StartupAdminManageHistoryList = Loadable(
  lazy(() => import('./pages/startup/manage-startup/ManageHistoryList'))
)
const StartupAdminTicketsList = Loadable(
  lazy(() => import('./pages/startup/tickets/TicketsList'))
)
const StartupAdminTicketsDetails = Loadable(
  lazy(() => import('./pages/startup/tickets/TicketsDetails'))
)
const StartupAdminNewsList = Loadable(
  lazy(() => import('./pages/startup/news/NewsList'))
)
const StartupAdminSetting = Loadable(
  lazy(() => import('./pages/startup/Setting'))
)

const InvestorAdminInvestorVerificationList = Loadable(
  lazy(() =>
    import('./pages/investor/investor-verification/InvestorVerificationList')
  )
)
const InvestorAdminInvestorVerificationDetails = Loadable(
  lazy(() =>
    import('./pages/investor/investor-verification/InvestorVerificationDetails')
  )
)
const InvestorAdminManageInvestorList = Loadable(
  lazy(() => import('./pages/investor/manage-investor/ManageInvestorList'))
)
const InvestorAdminRequestInvestList = Loadable(
  lazy(() => import('./pages/investor/manage-investor/RequestInvestList'))
)
const InvestorAdminManageInvestorDetails = Loadable(
  lazy(() => import('./pages/investor/manage-investor/ManageInvestorDetails'))
)
const InvestorAdminTicketsList = Loadable(
  lazy(() => import('./pages/investor/tickets/TicketsList'))
)
const InvestorAdminTicketsDetails = Loadable(
  lazy(() => import('./pages/investor/tickets/TicketsDetails'))
)

const ConformerAdminDashboard = Loadable(
  lazy(() => import('./pages/conformer/DashboardApp'))
)
const ConformerAdminProfile = Loadable(
  lazy(() => import('./pages/conformer/Profile'))
)
const ConformerAdminSetting = Loadable(
  lazy(() => import('./pages/conformer/Setting'))
)

const SupervisorAdminDashboard = Loadable(
  lazy(() => import('./pages/supervisor/DashboardApp'))
)
const SupervisorAdminProfile = Loadable(
  lazy(() => import('./pages/supervisor/Profile'))
)
const SupervisorAdminSetting = Loadable(
  lazy(() => import('./pages/supervisor/Setting'))
)

const NotFound = Loadable(lazy(() => import('./pages/Page404')))
